/**
 * Fleet-timezone date arithmetic.
 *
 * `work_order.due_date` is a **zone-less `date`**. Comparing it against the
 * server clock flips a day for anyone outside the fleet's zone, which is
 * exactly the class of arithmetic the sponsor already does not trust. So:
 *
 * - `overdueDays` and `dueThisWeek` are computed **once, on the server**, in the
 *   configured fleet timezone, and shipped as numbers.
 * - The browser never recomputes them. It formats what it was given.
 *
 * These helpers live in the shared package so the rule is testable without a
 * database and so the client can assert it is not doing the arithmetic.
 */

/**
 * The fleet's timezone. **The single definition — import it, never spell it.**
 *
 * Ruled by Eden, 2 September 2026 (TRBUD-98). It lives in `packages/contract`
 * because that is the one package both the server and the browser import, so
 * there is nowhere lower to put it.
 *
 * Two constants used to be live and neither looked wrong alone:
 * `apps/api/src/env.ts` defaulted to Eastern while
 * `packages/db/src/import-mainx-work-orders.ts` declared Central, and
 * `0105_downtime_rolling_window.sql` hard-coded Eastern into
 * `v_asset_downtime_by_period`. The failure was silent and month-shaped: a
 * stoppage at 23:30 Central on the last day of a month is 00:30 Eastern the next
 * day, so downtime hours landed in a different month depending on which constant
 * the reader had used. `fleet-timezone.test.ts` is the guard that stops a second
 * copy reappearing.
 *
 * `FLEET_TIMEZONE` is the DEFAULT, not the override: `readConfig()` still lets
 * `FLEET_TIMEZONE` in the environment win, because a second yard in another zone
 * is a configuration change and not a code change.
 *
 * ## The SQL side cannot import this, and that is a known compromise
 *
 * A view cannot read an environment variable, and a `tb_fleet_timezone()`
 * function would need its own EXECUTE grant for a constant. So
 * `v_asset_downtime_by_period` carries the zone as a literal, kept in step by
 * the month-boundary case in
 * `apps/api/src/read/asset-downtime-window.dbtest.test.ts` rather than by
 * anybody remembering. Changing the fleet zone means changing this constant AND
 * writing a migration that replaces that view.
 */
export const FLEET_TIMEZONE = "America/Chicago";

/**
 * "Today" in a named IANA zone, as a zone-less YYYY-MM-DD string.
 *
 * Uses `Intl` with an explicit `timeZone` rather than any offset arithmetic: an
 * offset is wrong twice a year, and `FLEET_TIMEZONE` is the fleet zone.
 */
export function todayInZone(now: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Whole days between two zone-less dates. Positive when `b` is later. */
export function dayDifference(a: string, b: string): number {
  const msPerDay = 86_400_000;
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / msPerDay);
}

/**
 * Days overdue, or `null` when not overdue (including "due today" and "no due
 * date"). Never negative — a caller that wants "days until due" should ask for
 * `dayDifference` and say so.
 */
export function overdueDays(dueDate: string | null, today: string): number | null {
  if (!dueDate) return null;
  const diff = dayDifference(dueDate, today);
  return diff > 0 ? diff : null;
}

/**
 * A zone-less date plus whole calendar months, clamping a day the destination
 * month does not have to its last day: 2028-02-29 + 12 is 2029-02-28, and
 * 2026-08-31 + 6 is 2027-02-28. Returns `null` for anything that is not a real
 * `YYYY-MM-DD` or a positive whole number of months, so a half-typed date in a
 * form shows nothing rather than a guess.
 *
 * TRBUD-342: the browser copy of `addCalendarMonths` in
 * `packages/db/src/maintenance-policy.ts`, for the inspection form's "Next due"
 * preview. The SAVED due date is computed by `record_compliance_inspection` with
 * Postgres month arithmetic, which clamps the same way.
 * `packages/db/src/inspection-due-date-parity.dbtest.test.ts` holds all three
 * together (this, the db copy, and the routine), because neither package can
 * import the other: the same arrangement as `isPhoneShaped`.
 *
 * UTC throughout and no `new Date(string)` on the input: V8 rolls an impossible
 * day forward (2026-02-30 becomes 2 March), so the parts are read by hand.
 */
export function addCalendarMonths(date: string, months: number): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match || !Number.isInteger(months) || months < 1) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) {
    return null;
  }
  const destination = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(destination.getUTCFullYear(), destination.getUTCMonth() + 1, 0),
  ).getUTCDate();
  destination.setUTCDate(Math.min(day, lastDay));
  return destination.toISOString().slice(0, 10);
}

/**
 * How many days "due this week" covers, counting today: today and the next six
 * (TRBUD-337, audit finding 11). The API's `dueThisWeekPredicate` is the SQL
 * twin of `isDueThisWeek` and reads this same constant.
 */
export const DUE_SOON_DAYS = 7;

/**
 * "Due this week": today and the next six days (`DUE_SOON_DAYS` in all), and not
 * already overdue. It was today through today + 7 until TRBUD-337, which is
 * eight days; audit finding 11.
 */
export function isDueThisWeek(dueDate: string | null, today: string): boolean {
  if (!dueDate) return false;
  const diff = dayDifference(today, dueDate);
  return diff >= 0 && diff < DUE_SOON_DAYS;
}

/**
 * Split a zone-less date, or return null if it is not one.
 *
 * The shape has to be *validated*, not just destructured: `"not-a-date"` splits
 * into three truthy parts, and a presence check would let it through to
 * `Number()` and render "NaN/NaN". A caller that gets null renders the raw
 * string, which is at least legible.
 */
function splitIsoDate(date: string): { year: string; month: string; day: string } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const [, year, month, day] = match;
  return { year: year!, month: month!, day: day! };
}

/** "8/12" — the queue's short due label, from a zone-less date, no Date object. */
export function formatShortDate(date: string): string {
  const parts = splitIsoDate(date);
  if (!parts) return date;
  return `${Number(parts.month)}/${Number(parts.day)}`;
}

/** "8/12/2026" — the detail meta row's due label. */
export function formatMediumDate(date: string): string {
  const parts = splitIsoDate(date);
  if (!parts) return date;
  return `${Number(parts.month)}/${Number(parts.day)}/${parts.year}`;
}

/**
 * An instant, or `null` when the string is not one. **The single NaN check.**
 *
 * `new Date("oops")` is not an error, it is an Invalid Date — a real object that
 * answers every method, so nothing fails until `Intl.DateTimeFormat.format`
 * raises `RangeError: Invalid time value` several frames later. That throw is the
 * hazard these formatters exist to contain: they are called inside a `.map()`
 * over a list, so one bad row does not cost one row, it costs the whole card.
 * The Timeline blanks and reads as "no events" rather than as "one timestamp is
 * broken" (TRBUD-248).
 *
 * `formatRelativeAge` is the same class with a quieter symptom: arithmetic on
 * `NaN` never throws, so it printed "NaNy" instead.
 *
 * Latent rather than live today — every timestamp the API sends comes from a
 * non-null `timestamptz` — which is exactly why it is worth closing now: the
 * cost is one guard, and the failure it prevents is silent, total, and looks
 * like empty data.
 *
 * Deliberately NOT a try/catch at the call sites. A throw would still have to be
 * caught once per call, six of them in `WorkOrder.tsx` alone, and the seventh
 * would be forgotten. These functions are made total instead, so there is
 * nothing to catch. Note `apps/api` calls two of them as well
 * (`read/insights.ts`, `read/work-order-detail.ts`), where the same throw is a
 * 500 on a read rather than a blank card.
 */
function instantOf(iso: string): Date | null {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * Wall-clock time in the fleet zone, "09:18". For timeline rows.
 *
 * "time unknown" rather than the raw string, because this one renders into a
 * fixed 52px column beside the event — see `.timeline-row` — and an arbitrary
 * string does not fit it. The DAY HEADING below makes the opposite choice for
 * the opposite reason; both are stated where they are made.
 */
export function formatZoneTime(iso: string, timeZone: string): string {
  const at = instantOf(iso);
  if (at === null) return "time unknown";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(at);
}

/**
 * The calendar date an **instant** fell on, in the fleet zone: "8/12/2026".
 *
 * Distinct from `formatMediumDate`, which takes an already-zone-less `date`
 * column. This one takes a `timestamptz` — a posted-at, a worked-at — and answers
 * "which day was that here?", which is not the same question as "what day is
 * stored". Formatting an instant with `formatMediumDate` would require slicing the
 * ISO string, and that slice is UTC: a cost posted at 21:00 EDT would read as the
 * next day.
 */
export function formatZoneDate(iso: string, timeZone: string): string {
  const at = instantOf(iso);
  if (at === null) return "date unknown";
  return formatMediumDate(todayInZone(at, timeZone));
}

/** "8/12" form of the above, for narrow columns. */
export function formatZoneShortDate(iso: string, timeZone: string): string {
  const at = instantOf(iso);
  if (at === null) return "date unknown";
  return formatShortDate(todayInZone(at, timeZone));
}

/**
 * "Wed, Sep 2, 2026" — the day heading above a run of timeline events.
 *
 * Takes an **instant** and answers which calendar day it fell on in `timeZone`,
 * the same question `formatZoneDate` asks and for the same reason: slicing the
 * ISO string would answer it in UTC, so an event at 19:05 Central would head up
 * the following day.
 *
 * Weekday-first and spelled rather than `formatMediumDate`'s "9/2/2026", because
 * this string exists to be *told apart at a glance* from the one above it — the
 * whole defect being fixed is two dates that looked alike. It is absolute rather
 * than relative ("Today" / "Yesterday"): the app has no relative-day idiom to
 * borrow, and a heading derived from `now` would need a clock to render, would go
 * silently wrong on a page left open past midnight, and would stop naming the day
 * at all — which is the thing the reader came for.
 */
export function formatZoneDayHeading(iso: string, timeZone: string): string {
  /*
   * Same graceful degradation as `splitIsoDate`: `Intl` THROWS on an invalid
   * date, so an unparseable `at` on one event would blank the whole timeline
   * card rather than one row of it. The raw string is at least legible.
   *
   * **This fallback is the RAW STRING, not a stated phrase, and the difference
   * from its five neighbours is deliberate.** A heading is a full-width line of
   * its own with room for whatever the string is, and it is the one place the
   * bad value can still be SEEN — every other formatter renders into a narrow
   * cell and returns a phrase. Pinned since 10 September by the case "an
   * unparseable timestamp costs one row, not the whole timeline"; TRBUD-248
   * made the other five total and deliberately left this pair alone, because
   * changing it to "Undated" is a behaviour change with an owner rather than a
   * tidy-up.
   */
  const at = instantOf(iso);
  if (at === null) return iso;
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(at);
}

/** One calendar day's worth of events, and the heading that names it. */
export interface ZoneDayGroup<T> {
  /** The zone-less day in `timeZone`, "2026-09-02". The grouping key. */
  day: string;
  /** "Wed, Sep 2, 2026". */
  heading: string;
  events: T[];
}

/**
 * Group timestamped rows under one heading per calendar day in `timeZone`.
 *
 * Groups **consecutive runs**, not every row sharing a day, and that is the
 * load-bearing choice. The caller's order is preserved exactly — the timeline
 * read is `ORDER BY occurred_at ASC, event_id ASC`, so consecutive runs and
 * per-day buckets are the same partition on real input, and on input that is
 * somehow out of order this emits a repeated heading instead of silently
 * resequencing the record. A timeline that reorders events to tidy its headings
 * would be a worse bug than the one being fixed.
 *
 * The day key comes from `todayInZone`, so this is the same zone arithmetic the
 * due-date rules use rather than a second implementation of it.
 */
export function groupByZoneDay<T extends { at: string }>(
  events: readonly T[],
  timeZone: string,
): ZoneDayGroup<T>[] {
  const groups: ZoneDayGroup<T>[] = [];
  for (const event of events) {
    const at = instantOf(event.at);
    // An unparseable timestamp keys on its own raw string, so it lands in a
    // group of its own rather than being folded into whichever day it happened
    // to follow.
    const day = at === null ? event.at : todayInZone(at, timeZone);
    const open = groups[groups.length - 1];
    if (open && open.day === day) {
      open.events.push(event);
      continue;
    }
    groups.push({ day, heading: formatZoneDayHeading(event.at, timeZone), events: [event] });
  }
  return groups;
}

/**
 * "MAY 2026" — the asset-history month tag.
 *
 * "UNDATED" rather than "undated": every value this function returns is upper
 * case, they stack in one narrow column, and a single lower-case entry among
 * them reads as a rendering fault rather than as a missing date.
 */
export function formatMonthTag(iso: string, timeZone: string): string {
  const at = instantOf(iso);
  if (at === null) return "UNDATED";
  return new Intl.DateTimeFormat("en-US", { timeZone, month: "short", year: "numeric" }).format(at).toUpperCase();
}

/**
 * "38m" / "4h" / "3d" / "9d" — coarse relative age for "opened Nd ago" and the
 * last-update line. Deliberately coarse: an exact age invites reading precision
 * into a value the source may only know to the hour.
 *
 * The odd one out of the seven: `NaN` arithmetic throws nothing, so an
 * unparseable input fell through every comparison in the ladder (`NaN < 60` is
 * false, and so is every other) and printed "NaNy" — a value, in the place a
 * value belongs, that nobody reads as an error. "age unknown" says it.
 */
export function formatRelativeAge(iso: string, now: Date): string {
  const at = instantOf(iso);
  if (at === null) return "age unknown";
  const ms = now.getTime() - at.getTime();
  if (ms < 0) return "just now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

/** Freshness threshold for Samsara/Sylectus reads, in ms. 30 minutes. */
export const STALE_AFTER_MS = 30 * 60 * 1000;

/** Matches `classifyFreshness` in packages/integrations. Unknown when never seen. */
export function classifyFreshness(
  observedAt: string | null,
  now: Date,
  staleAfterMs = STALE_AFTER_MS,
): "active" | "stale" | "unknown" {
  if (observedAt === null) return "unknown";
  const observed = Date.parse(observedAt);
  if (Number.isNaN(observed)) return "unknown";
  return now.getTime() - observed <= staleAfterMs ? "active" : "stale";
}

/** Snapshot age is fixed at its effective opening, however late it is viewed. */
export function classifySnapshotFreshness(
  observedAt: string | null,
  referenceAt: string | null,
  staleAfterMs = STALE_AFTER_MS,
): "active" | "stale" | "unknown" {
  if (referenceAt === null || !Number.isFinite(Date.parse(referenceAt))) return "unknown";
  return classifyFreshness(observedAt, new Date(referenceAt), staleAfterMs);
}

/**
 * The event a work-order snapshot is fixed at, as the words read on screen.
 *
 * "opening" is the default and every caller before TRBUD-430 meant it. The
 * Repair complete fix passes "repair complete", so one rule ages both facts and
 * the two lines read alike ("observed 8m after repair complete").
 */
export type SnapshotEvent = "opening" | "repair complete";

/** A snapshot's observation age, relative to the event that owns the fact. */
export function formatSnapshotAge(
  observedAt: string | null,
  referenceAt: string | null,
  event: SnapshotEvent = "opening",
): string | null {
  if (observedAt === null) return null;
  if (!Number.isFinite(Date.parse(observedAt))) return "observation time unknown";
  if (referenceAt === null || !Number.isFinite(Date.parse(referenceAt))) return `${event} reference unknown`;
  const delta = Date.parse(referenceAt) - Date.parse(observedAt);
  if (Math.abs(delta) < 60_000) return `at ${event}`;
  if (delta < 0) return `${formatRelativeAge(referenceAt, new Date(observedAt))} after ${event}`;
  return `${formatRelativeAge(observedAt, new Date(referenceAt))} before ${event}`;
}

export function formatSnapshotFreshness(
  observedAt: string | null,
  referenceAt: string | null,
  staleAfterMs = STALE_AFTER_MS,
  event: SnapshotEvent = "opening",
): string {
  const age = formatSnapshotAge(observedAt, referenceAt, event);
  if (age === null) return "never seen";
  if (referenceAt === null) return age;
  const state = classifySnapshotFreshness(observedAt, referenceAt, staleAfterMs);
  return `observed ${age}${state === "stale" ? ` — STALE at ${event}` : ""}`;
}

/**
 * "as of 12:41" / "as of 3d ago — STALE" / "never seen".
 *
 * `staleAfterMs` defaults to the 30-minute vendor-read window; a position
 * readout passes `POSITION_TTL_MINUTES * 60_000` so the Assets table and the
 * Live map agree about when a fix stops being current.
 */
export function formatFreshness(
  observedAt: string | null,
  now: Date,
  timeZone: string,
  staleAfterMs = STALE_AFTER_MS,
): string {
  const state = classifyFreshness(observedAt, now, staleAfterMs);
  if (state === "unknown" || observedAt === null) return "never seen";
  if (state === "active") return `as of ${formatZoneTime(observedAt, timeZone)}`;
  return `as of ${formatRelativeAge(observedAt, now)} ago — STALE`;
}
